import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { messageAPI } from '../services/api';
import axios from 'axios';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;

      try {
        const response = await messageAPI.getUnreadCount();
        setUnreadCount(response.data.unreadCount);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
  }, [user]);

  // Fetch unread notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (!user) return;

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/notifications/unread/count', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotificationCount(response.data.unreadCount);
      } catch (error) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    fetchNotificationCount();
  }, [user]);

  // Listen for new messages via Socket.io
  useEffect(() => {
    if (!socket || !user) return;

    const handleNewMessage = (message) => {
      // Increment unread count if message is for current user and they're not on messages page
      if (message.receiverId === user.id && !window.location.pathname.startsWith('/messages')) {
        setUnreadCount(prev => prev + 1);
      }
    };

    const handleNewNotification = (notification) => {
      // Increment notification count
      setNotificationCount(prev => prev + 1);
    };

    socket.on('new_message', handleNewMessage);
    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, user]);

  // Listen for messages page visit to reset badge
  useEffect(() => {
    const handleMessagesPageVisited = () => {
      setUnreadCount(0);
    };

    window.addEventListener('messagesPageVisited', handleMessagesPageVisited);

    return () => {
      window.removeEventListener('messagesPageVisited', handleMessagesPageVisited);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-lg border-b border-pastel-purple-200 dark:border-gray-700 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-bold bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 bg-clip-text text-transparent hover:from-pastel-purple-600 hover:to-pastel-pink-600 transition-all"
          >
            FirmFlow
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            <NavLink to="/">{t('nav.dashboard')}</NavLink>
            <NavLink to="/invoices">{t('nav.invoices')}</NavLink>
            <NavLink to="/invoice-templates">{t('nav.templates')}</NavLink>
            <Link
              to="/messages"
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-pastel-purple-100 dark:hover:bg-gray-800 hover:text-pastel-purple-600 dark:hover:text-pastel-purple-400 transition-colors relative"
            >
              {t('nav.messages')}
              {(unreadCount + notificationCount) > 0 && (
                <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-gradient-to-r from-pastel-pink-500 to-pastel-purple-500 rounded-full animate-pulse">
                  {(unreadCount + notificationCount) > 99 ? '99+' : (unreadCount + notificationCount)}
                </span>
              )}
            </Link>
            <NavLink to="/settings">{t('nav.settings')}</NavLink>
            {user?.role === 'super_admin' && (
              <NavLink to="/users">{t('nav.users')}</NavLink>
            )}
          </div>

          {/* Right side: Language switcher, User info, Dark mode toggle, Logout/mobile menu button */}
          <div className="flex items-center space-x-3">
            {/* Language switcher - desktop only */}
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>

            {/* User info - desktop only */}
            <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-pastel-purple-50 dark:bg-gray-800 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pastel-purple-400 to-pastel-pink-400 flex items-center justify-center text-white font-semibold text-sm">
                {user?.username?.[0]?.toUpperCase()}
              </div>
              <div className="text-sm">
                <div className="font-medium text-gray-900 dark:text-white">{user?.username}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-pastel-purple-100 dark:bg-gray-800 hover:bg-pastel-purple-200 dark:hover:bg-gray-700 transition-colors"
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {darkMode ? (
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-pastel-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* Desktop logout button */}
            <button
              onClick={handleLogout}
              className="hidden md:block px-4 py-2 rounded-lg bg-gradient-to-r from-pastel-blue-400 to-pastel-purple-400 text-white font-medium hover:from-pastel-pink-500 hover:to-pastel-purple-500 transition-all shadow-md hover:shadow-lg"
            >
              {t('nav.logout')}
            </button>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-pastel-purple-100 dark:bg-gray-800 hover:bg-pastel-purple-200 dark:hover:bg-gray-700 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-700 dark:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-pastel-purple-200 dark:border-gray-700">
            {/* User info - mobile */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-pastel-purple-100 dark:bg-gray-800 rounded-lg mb-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pastel-purple-400 to-pastel-pink-400 flex items-center justify-center text-white font-semibold text-sm">
                {user?.username?.[0]?.toUpperCase()}
              </div>
              <div className="text-sm">
                <div className="font-medium text-gray-900 dark:text-white">{user?.username}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>

            {/* Language switcher - mobile */}
            <div className="px-3 py-2">
              <LanguageSwitcher />
            </div>

            {/* Navigation links - mobile */}
            <MobileNavLink to="/" onClick={() => setMobileMenuOpen(false)}>{t('nav.dashboard')}</MobileNavLink>
            <MobileNavLink to="/invoices" onClick={() => setMobileMenuOpen(false)}>{t('nav.invoices')}</MobileNavLink>
            <MobileNavLink to="/invoice-templates" onClick={() => setMobileMenuOpen(false)}>{t('nav.templates')}</MobileNavLink>
            <Link
              to="/messages"
              onClick={() => setMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-lg text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-pastel-purple-100 dark:hover:bg-gray-800 hover:text-pastel-purple-600 dark:hover:text-pastel-purple-400 transition-colors relative"
            >
              {t('nav.messages')}
              {(unreadCount + notificationCount) > 0 && (
                <span className="absolute top-1 right-2 flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-gradient-to-r from-pastel-pink-500 to-pastel-purple-500 rounded-full animate-pulse">
                  {(unreadCount + notificationCount) > 99 ? '99+' : (unreadCount + notificationCount)}
                </span>
              )}
            </Link>
            <MobileNavLink to="/settings" onClick={() => setMobileMenuOpen(false)}>{t('nav.settings')}</MobileNavLink>
            {user?.role === 'super_admin' && (
              <MobileNavLink to="/users" onClick={() => setMobileMenuOpen(false)}>{t('nav.users')}</MobileNavLink>
            )}

            {/* Logout button - mobile */}
            <button
              onClick={handleLogout}
              className="w-full mt-3 px-4 py-2 rounded-lg bg-gradient-to-r from-pastel-blue-400 to-pastel-purple-400 text-white font-medium hover:from-pastel-pink-500 hover:to-pastel-purple-500 transition-all shadow-md hover:shadow-lg"
            >
              {t('nav.logout')}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

// NavLink component for desktop navigation items
const NavLink = ({ to, children }) => {
  return (
    <Link
      to={to}
      className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-pastel-purple-100 dark:hover:bg-gray-800 hover:text-pastel-purple-600 dark:hover:text-pastel-purple-400 transition-colors"
    >
      {children}
    </Link>
  );
};

// MobileNavLink component for mobile menu items
const MobileNavLink = ({ to, onClick, children }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-3 py-2 rounded-lg text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-pastel-purple-100 dark:hover:bg-gray-800 hover:text-pastel-purple-600 dark:hover:text-pastel-purple-400 transition-colors"
    >
      {children}
    </Link>
  );
};

export default Navbar;
